import { Controller } from "@hotwired/stimulus"

// Format + resolution + quality, kept in step with the chosen engine.
//
// The server composes the real frame (Lookbook::Frame) — this only mirrors the
// same arithmetic so the operator sees the pixels before submitting, and hides
// the settings the selected engine cannot honour: gpt-image-2 stops at 2048 per
// side, Seedream will not go below 2K, and only gpt-image-2.5 takes the quality
// tiers above "high". Everything it needs rides in on
// data-frame-picker-config-value, so the two can't drift apart in wording.
export default class extends Controller {
  static targets = ["format", "resolution", "engine", "quality", "hint"]
  // `baseSize` is the frame a form is editing rather than composing — the
  // Edition Studio passes its parent's exact "WxH". It supplies the ratio where
  // there is no format picker, and it is what "same as original" means.
  static values  = { config: Object, baseSize: String }

  connect() {
    this.syncEngine()
  }

  // Engine changed: re-offer what it can actually do.
  syncEngine() {
    this.syncResolutions()
    this.syncQualities()
  }

  // The tiers this engine renders, keeping the operator's pick when it survives
  // and landing on the nearest one when not.
  syncResolutions() {
    if (!this.hasResolutionTarget) return

    const allowed = this.allowedResolutions()
    const wanted  = this.resolutionTarget.value
    // A form may offer a "leave it exactly as it is" choice ahead of the tiers
    // — the Edition Studio does, because a refine keeps its parent's crop
    // unless asked otherwise. It belongs to no engine, so it survives every
    // re-offer and stays selected if that is where the operator left it.
    const keepAsIs = this.resolutionTarget.querySelector('option[value=""]')
    const keep     = allowed.includes(wanted) ? wanted
                   : (keepAsIs && wanted === "") ? ""
                   : this.nearest(wanted, allowed)

    this.resolutionTarget.innerHTML = ""
    if (keepAsIs) {
      keepAsIs.selected = keep === ""
      this.resolutionTarget.appendChild(keepAsIs)
    }
    allowed.forEach((key) => {
      const option = document.createElement("option")
      option.value       = key
      option.textContent = this.config.labels[key] || key.toUpperCase()
      option.selected    = key === keep
      this.resolutionTarget.appendChild(option)
    })

    this.update()
  }

  // The quality tiers this engine takes. Same rule as the resolutions: an
  // operator sitting on "Max" who switches to an engine without it keeps the
  // best that engine has rather than silently submitting a word it will
  // reject — the server steps the same value down (ImageClient.quality_for).
  syncQualities() {
    if (!this.hasQualityTarget || !this.hasEngineTarget) return

    const allowed = (this.config.qualities || {})[this.engineTarget.value]
    if (!allowed) return

    const labels = this.config.qualityLabels || {}
    const wanted = this.qualityTarget.value
    const keep   = allowed.includes(wanted) ? wanted : allowed[allowed.length - 1]

    this.qualityTarget.innerHTML = ""
    allowed.forEach((key) => {
      const option = document.createElement("option")
      option.value       = key
      option.textContent = labels[key] || key
      option.selected    = key === keep
      this.qualityTarget.appendChild(option)
    })
  }

  update() {
    if (!this.hasHintTarget) return

    const [w, h] = this.frame()
    this.hintTarget.textContent = w ? `Renders at ${w} × ${h} px` : ""
  }

  // — internals —

  get config() {
    return this.configValue || {}
  }

  // The frame budget the selected engine renders under — its provider for
  // most, its own profile where a model outgrew its family (gpt-image-2.5).
  get profile() {
    if (!this.hasEngineTarget) return null
    return (this.config.profiles || {})[this.engineTarget.value] || null
  }

  // Handed down from Lookbook::Frame.resolutions_for rather than re-derived
  // from the side limits: Seedream's real ceiling is its AREA cap, so the tiers
  // it offers are a curated list the arithmetic here would get wrong.
  allowedResolutions() {
    return (this.config.tiers || {})[this.profile] || Object.keys(this.config.resolutions || {})
  }

  nearest(key, allowed) {
    const resolutions = this.config.resolutions || {}
    const target = resolutions[key]
    if (!target || !allowed.length) return allowed[0]

    return allowed.reduce((best, candidate) =>
      Math.abs(resolutions[candidate] - target) < Math.abs(resolutions[best] - target) ? candidate : best
    )
  }

  // The same clamp-then-snap-then-area-fit the server does, so the hint is the
  // frame. The area step is not decoration: a 1:1 frame at 3K is 9.4 MP, over
  // gpt-image-2.5's budget, and comes back smaller than the tier promises.
  frame() {
    const ratio = this.ratio()
    if (!ratio) return []

    // No tier chosen means "leave this frame exactly as it is".
    const picked = this.hasResolutionTarget ? this.resolutionTarget.value : ""
    if (!picked && this.baseDimensions()) return this.baseDimensions()

    const edge = (this.config.resolutions || {})[picked]
    if (!edge) return []

    const [rw, rh] = ratio
    const max  = (this.config.maxSide || {})[this.profile] || edge
    const long = Math.min(edge, max)

    const [w, h] = rw > rh
      ? [this.snap(long), this.snap((long * rh) / rw)]
      : [this.snap((long * rw) / rh), this.snap(long)]

    return this.areaFit(w, h)
  }

  // The shape to re-cut: the chosen catalogue format where a form offers one,
  // otherwise the frame being edited.
  ratio() {
    if (this.hasFormatTarget) return (this.config.formats || {})[this.formatTarget.value]
    return this.baseDimensions()
  }

  baseDimensions() {
    const match = /^(\d+)x(\d+)$/.exec(this.baseSizeValue || "")
    return match ? [Number(match[1]), Number(match[2])] : null
  }

  // Mirrors Lookbook::Frame.area_fit — scale down until the frame is inside the
  // engine's pixel budget, keeping the ratio.
  areaFit(w, h) {
    const cap = (this.config.maxPixels || {})[this.profile]
    if (!cap || w * h <= cap) return [w, h]

    let scale = Math.sqrt(cap / (w * h))
    for (let i = 0; i < 50; i++) {
      const fw = this.snap(w * scale)
      const fh = this.snap(h * scale)
      if (fw * fh <= cap) return [fw, fh]
      scale *= 0.98
    }
    return [w, h]
  }

  snap(value) {
    const grid = this.config.grid || 16
    return Math.max(grid, Math.round(value / grid) * grid)
  }
}
